// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck https://atlassian.slack.com/archives/C02785CLSTW/p1658657213150179
/** @jsx jsx */
import React, { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { cssMap, jsx } from '@compiled/react';
import omit from 'lodash/omit';
import Icon from '@atlaskit/icon';
import BulletListIcon from '@atlaskit/icon/core/migration/list-bulleted--bullet-list';
import CalendarIcon from '@atlaskit/icon/core/migration/calendar';
import CodeIcon from '@atlaskit/icon/core/migration/angle-brackets--code';
import DashboardIcon from '@atlaskit/icon/core/migration/dashboard';
import GlobeIcon from '@atlaskit/icon/core/globe';
import GettingStartedPageIcon from '@atlaskit/icon/core/migration/compass--discover';
import EmailIcon from '@atlaskit/icon/core/migration/email';
import FeedbackIcon from '@atlaskit/icon/core/migration/feedback';
import ReportsIcon from '@atlaskit/icon/core/migration/chart-bar--graph-bar';
import IssuesIcon from '@atlaskit/icon/core/migration/work-items--issues';
import LabelIcon from '@atlaskit/icon/core/migration/tag--label';
import PeopleGroupIcon from '@atlaskit/icon/core/migration/people-group';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import QueuesIcon from '@atlaskit/icon/core/migration/pages--queues';
import IconSettings from '@atlaskit/icon/core/migration/settings';
import ArchiveIcon from '@atlaskit/icon/core/migration/archive-box--archive';
import Lozenge from '@atlaskit/lozenge';

import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import { SpotlightManager } from '@atlaskit/onboarding';
import {
	NavigationHeader,
	NestableNavigationContent,
	Section,
	SideNavigation,
	SkeletonItem,
} from '@atlaskit/side-navigation';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import {
	EntryKeys,
	ENTRYPOINT_ID_JIRA_PLATFORM,
} from '@atlassian/jira-feedback-collector/src/constants.tsx';
import { useFeedbackCollectorActions } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { Shortcuts } from '@atlassian/jira-navigation-apps-shortcuts-sidebar/src/ui/main.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { SpotlightTarget } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/spotlight-target/index.tsx';
import { useNavigationAutomationLegacyEnabledResource } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/navigation-automation-resource/index.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import {
	ContextualAnalyticsData,
	IssuesContextualAnalyticsData,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/contextual-analytics-data/index.tsx';
import { Issues } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/issues/main.tsx';
import { NestingMenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import { ProjectHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/project-header/main.tsx';
import { shouldRenderNesting } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/should-render/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import {
	matchPartialUrlPathname,
	matchUrlPathname,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { ConnectMenu } from '@atlassian/jira-navigation-apps-sidebar-connect/src/common/ui/connect-menu/main.tsx';
import { useProjectReportsConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project-reports/index.tsx';
import { useProjectSettingsConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project-settings/index.tsx';
import { useProjectConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project/index.tsx';
import { useProjectSettingsForgeAppsForMenu } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/project-settings/index.tsx';
import { useProjectForgeApps } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/project/index.tsx';
import { useServiceDeskQueueForgeApps } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/servicedesk-queue/index.tsx';
import { ForgeProjectSection } from '@atlassian/jira-navigation-apps-sidebar-forge/src/ui/project-section/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { INVITE_USERS_PROMPTS_KEY } from '@atlassian/jira-service-management-adoption-nudges/src/common/constants.tsx';
import { useShouldRenderInviteTeamPromptSpotlight } from '@atlassian/jira-service-management-adoption-nudges/src/services/index.tsx';
import { InviteUserPrompt } from '@atlassian/jira-service-management-adoption-nudges/src/ui/index.tsx';
import { HOME_ITEMS_IDS } from '@atlassian/jira-servicedesk-common/src/constants.tsx';
import { QUEUE_ROUTES } from '@atlassian/jira-servicedesk-navigation/src/utils/queues/index.tsx';
import { useIsGettingStartedPageEnabled } from '@atlassian/jira-servicedesk-getting-started-page-common/src/common/controllers/getting-started-page-enabled/useIsGettingStartedPageEnabled.tsx';
import { AlertsGlyph } from '@atlassian/jira-servicedesk-incident-management-common/src/assets/index.tsx';
import { usePracticeList } from '@atlassian/jira-servicedesk-queues-categorized-store/src/main.tsx';
import { ViewsNavItemOnboarding } from '@atlassian/jira-servicedesk-views-onboarding/src/ui/views-nav-item-onboarding/index.tsx';
import type { RouteName } from '@atlassian/jira-route-name-types/src/RouteName.tsx';
import { performanceRoute } from '@atlassian/jira-router-routes-project-settings-service-desk-virtual-agent-routes/src/performanceRoute.tsx';
import {
	ChangesGlyph,
	IncidentsGlyph,
	ProblemsGlyph,
	ServiceRequestsGlyph,
	ServicesGlyph,
	PostIncidentReviewsGlyph,
} from '@atlassian/jira-servicedesk-work-category/src/common/assets/glyph.tsx';
import {
	CHANGES,
	INCIDENTS,
	NO_CATEGORY,
	PROBLEMS,
	SERVICE_REQUEST,
	POST_INCIDENT_REVIEWS,
} from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import practiceMessages from '@atlassian/jira-servicedesk-work-category/src/common/messages.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useResource, generatePath, useRouter } from '@atlassian/react-resource-router';
import { OnCallGlyph } from '../../common/assets/glyph.tsx';
import { MENU_ID_SD_ITSM, SECTION_ID, FEEDBACK_GROUP_ID } from '../../common/constants.tsx';
import { messages } from '../../common/messages.tsx';
import { CustomerChannelsItem } from '../../common/ui/customer-channels-item/index.tsx';
import { CustomerSupportManagementItems } from '../../common/ui/customer-support-management-section/index.tsx';
import { InviteTeamItemNew } from '../../common/ui/invite-team-item-v2/index.tsx';
import { InviteTeamItem } from '../../common/ui/invite-team-item/index.tsx';
import { KnowledgeItem } from '../../common/ui/knowledge-item/main.tsx';
import { PracticeQueues } from '../../common/ui/practice-queues/main.tsx';
import { Reports } from '../../common/ui/reports/main.tsx';
import { SettingsClassic } from '../../common/ui/settings-classic/main.tsx';
import { useFeatures, useSettingFeatures } from '../../controllers/features/main.tsx';
import { useNestingStack } from '../../controllers/nesting-stack/index.tsx';
import { useViewsFeature } from '../../controllers/views-feature/index.tsx';

const styles = cssMap({
	menuItem: {
		height: '44px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-elem-after]': {
			opacity: 0,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover [data-item-elem-after], &:focus-within [data-item-elem-after]': {
			opacity: 1,
		},
	},
});

export const useThrowFeatures = withThrowOnError(useFeatures);
export const useThrowSettingFeatures = withThrowOnError(useSettingFeatures);
export const useThrowProject = withThrowOnError(useProject);
export const useThrowProjectConnectItems = withThrowOnError(useProjectConnectItems);
export const useThrowProjectReportsConnectItems = withThrowOnError(useProjectReportsConnectItems);
export const useThrowProjectSettingsConnectItems = withThrowOnError(useProjectSettingsConnectItems);

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4/jsm-project-menu
 */
export const MenuPractices = () => {
	const { formatMessage } = useIntl();
	const [forgeQueueParentStack] = useState(() => [SECTION_ID.QUEUES]);
	const [forgeSettingsParentStack, setForgeSettingsParentStack] = useState(() => [
		SECTION_ID.SETTINGS,
	]);
	const [{ route }] = useRouter();

	const { data: features } = useThrowFeatures();
	const { data: settingFeatures } = useThrowSettingFeatures();
	const { data: project } = useThrowProject();
	const [practiceList] = usePracticeList();
	const { openFeedbackCollector } = useFeedbackCollectorActions();
	const cloudId = useCloudId();

	const { data: projectConnectItems, stack: connectProjectStack } = useThrowProjectConnectItems();
	const { data: connectReportsItems, stack: connectReportsStack } =
		useThrowProjectReportsConnectItems();

	const { data: connectSettingsItems, stack: connectSettingsStack } =
		useThrowProjectSettingsConnectItems();

	const { stack: forgeProjectStack } = useProjectForgeApps('forge-spa-project');
	const { stack: forgeProjectServicedeskStack } = useProjectForgeApps(
		'forge-spa-project-servicedesk',
	);
	const { data: forgeSettingsItems, stack: forgeProjectSettingsStack } =
		useProjectSettingsForgeAppsForMenu(
			'project-settings-servicedesk-forge-apps',
			forgeSettingsParentStack,
		);

	const { stack: forgeQueueStack } = useServiceDeskQueueForgeApps(
		'forge-spa-servicedesk-queue',
		forgeQueueParentStack,
	);

	const ecosystemItems = useMemo(
		() => ({
			forge: forgeSettingsItems,
			connect: connectSettingsItems,
		}),
		[connectSettingsItems, forgeSettingsItems],
	);

	const { enabled: isLegacyAutomationEnabled } =
		useNavigationAutomationLegacyEnabledResource().data || {};

	const [stack, setStack] = useNestingStack({
		connectStack: connectProjectStack || connectReportsStack || connectSettingsStack,
		forgeStack:
			forgeProjectStack ||
			forgeProjectServicedeskStack ||
			forgeProjectSettingsStack ||
			forgeQueueStack,
		knowledgeType: features && features.knowledge.type,
		isLegacyAutomationEnabled: !!isLegacyAutomationEnabled,
		isTeamManagedProject: project?.isSimplified ?? false,
	});

	const { data: projectContext } = useResource(projectContextResource);
	const {
		shouldUseJSM,
		loading: isOgTenantInfoLoading,
		error: ogTenantInfoError,
	} = useOgTenantInfo();
	const [isLoading, isGettingStatedPageEnabled] = useIsGettingStartedPageEnabled();
	const { isInviteTeamSpotlightEnabled, loadInviteTeamResource, onInviteTeamOnboardingCTAClick } =
		useShouldRenderInviteTeamPromptSpotlight();

	useEffect(() => {
		project?.isSimplified
			? // @ts-expect-error - TS2322 - Type '"apps"' is not assignable to type '"settings"'.
				setForgeSettingsParentStack([SECTION_ID.SETTINGS, 'apps'])
			: setForgeSettingsParentStack([SECTION_ID.SETTINGS]);
	}, [project?.isSimplified]);

	const feedbackButtonRef = useRef<HTMLButtonElement>(null);

	const onSubmitFeedbackClick = useCallback(() => {
		const additionalFields = [{ id: EntryKeys.CLOUD_ID, value: cloudId }];
		openFeedbackCollector({
			feedbackGroupId: FEEDBACK_GROUP_ID,
			entrypointId: ENTRYPOINT_ID_JIRA_PLATFORM,
			feedbackContext: additionalFields,
			additionalFields,
			triggerRef: feedbackButtonRef,
		});
	}, [openFeedbackCollector, cloudId]);

	const viewsFeature = useViewsFeature({ features });

	const archivedProjectItems = useMemo(() => {
		if (features?.hasArchivedIssues !== true) {
			return null;
		}

		const archivedIssuesUrl =
			project && project?.key
				? `/jira/servicedesk/projects/${project?.key}/archived-work-items`
				: null;
		return (
			<Section hasSeparator>
				<MenuItem
					analytics={{ itemId: 'archives' }}
					href={archivedIssuesUrl}
					iconBefore={<ArchiveIcon spacing="spacious" label="" />}
					iconAfter={<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>}
					selectedOn={['servicedesk-archived-work-items']}
					testId="navigation-apps-sidebar-servicedesk.ui.menu-practices.archives-link"
				>
					{fg('itsm-issue-renaming')
						? formatMessage(messages.itemArchivesIssueTermRefresh)
						: formatMessage(messages.itemArchives)}
				</MenuItem>
			</Section>
		);
	}, [features?.hasArchivedIssues, project, formatMessage]);

	if (
		features == null ||
		project == null ||
		practiceList == null ||
		projectConnectItems == null ||
		isOgTenantInfoLoading
	) {
		return fg('jsm_flash_add_more_ufo_holds_to_nav') ? (
			<UFOLoadHold name="menu-practice-loading-tenant-info">
				<SkeletonItem />
			</UFOLoadHold>
		) : (
			<SkeletonItem />
		);
	}

	const urlPath = `/jira/servicedesk/projects/${project.key}`;
	const classicSettingsUrl = `/jira/servicedesk/projects/${project.key}/settings/details`;

	const shouldShowServices = Boolean(settingFeatures?.hasServices);

	const shouldShowOpsGenieAlert = !!features?.opsgenieAlert.enabled && !!features.opsgenieAlert.url;

	const shouldShowOpsGenieOnCall =
		!!features?.opsgenieOnCall.enabled && !!features.opsgenieOnCall.url;

	const isJSMAlertOnCallEnabled = shouldUseJSM && !ogTenantInfoError;

	const isCustomerSupportManagementEnabled = Boolean(settingFeatures?.hasCustomerSupportManagement);
	const isDeveloperEscalationsEnabled = Boolean(settingFeatures?.hasDeveloperEscalation);
	const isProductsAndEntitlementsEnabled = Boolean(settingFeatures?.hasProductsAndEntitlements);
	const hasNotificationLogs = fg('jcs_master_flag') ? !!features.hasNotificationLogs : true;

	const BackMenuItem = {
		href: urlPath,
		// @ts-expect-error - TS7006 - Parameter 'ev' implicitly has an 'any' type.
		onClick: (ev) => ev.preventDefault(),
		title: formatMessage(messages.backToProject),
	};

	const knowledgeInsightsTitle =
		// Returns true if any Knowledge & Insights elements exist.
		practiceList.includes(CHANGES) ||
		shouldRenderNesting(
			features.knowledge.enabled && !!features.knowledge.type,
			// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'.
			stack,
			'knowledge',
		) ||
		// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'.
		shouldRenderNesting(features.hasReports, stack, SECTION_ID.REPORTS)
			? formatMessage(messages.knowledgeInsights)
			: undefined;

	const changeCalendar = practiceList.includes(CHANGES) && (
		<SpotlightTarget name={HOME_ITEMS_IDS.CHANGE_CALENDAR_SPOTLIGHT}>
			<MenuItem
				analytics={{ itemId: 'changeCalendar' }}
				href={`${urlPath}/change-calendar`}
				iconBefore={<CalendarIcon spacing="spacious" label="" />}
				selectedOn={matchUrlPathname()}
			>
				{formatMessage(messages.changeCalendar)}
			</MenuItem>
		</SpotlightTarget>
	);

	/* NOTE: This SpotlightTarget can be used throughout Jira not just within
	 * the current package. They're often used for onboarding or changeboarding
	 * experiences and allow an Atlaskit spotlight to be attached to page elements.
	 * If you need to change this, make sure you check where the target ID is being used
	 * and contact the relevant team as necessary. This Sourcegraph link should help :)
	 * http://go/src/SERVICES_SPOTLIGHT|services-spotlight
	 */
	const services = shouldShowServices && (
		<SpotlightTarget name={HOME_ITEMS_IDS.SERVICES_SPOTLIGHT}>
			<MenuItem
				analytics={{ itemId: 'services' }}
				href={`${urlPath}/services`}
				// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
				iconBefore={<Icon glyph={ServicesGlyph} label="" />}
				selectedOn={matchUrlPathname()}
			>
				{formatMessage(messages.services)}
			</MenuItem>
		</SpotlightTarget>
	);

	const hasOperationsSectionItems = [
		changeCalendar,
		services,
		shouldShowOpsGenieAlert,
		shouldShowOpsGenieOnCall,
	].some(Boolean);

	const feedback = (
		<MenuItem
			analytics={{ itemId: 'feedback' }}
			iconBefore={<FeedbackIcon spacing="spacious" label="" />}
			onClick={onSubmitFeedbackClick}
			ref={feedbackButtonRef}
		>
			{formatMessage(messages.feedback)}
		</MenuItem>
	);

	const jsmProjectSummary = features.hasProjectSummary && (
		<>
			<MenuItem
				analytics={{ itemId: 'project-summary' }}
				href={`${urlPath}/summary`}
				iconBefore={
					fg('jsm_project_summary_milestone_2') ? (
						<GlobeIcon label="" />
					) : (
						<DashboardIcon spacing="spacious" label="" />
					)
				}
				selectedOn={['servicedesk-project-summary']}
			>
				{formatMessage(messages.itemProjectSummary)}
			</MenuItem>
		</>
	);

	const JsmViewsOnboarding = fg('jsm_views_of_work_phase_1_onboarding')
		? ViewsNavItemOnboarding
		: Fragment;
	const jsmViews = viewsFeature.isEnabled && (
		<JsmViewsOnboarding>
			<MenuItem
				analytics={{ itemId: 'views' }}
				href={viewsFeature.url}
				iconBefore={<BulletListIcon spacing="spacious" label="" />}
				selectedOn={['servicedesk-boards', 'servicedesk-calendar']}
			>
				{formatMessage(messages.itemViews)}
			</MenuItem>
		</JsmViewsOnboarding>
	);

	const GettingStartedPageMenuItem = !isLoading && isGettingStatedPageEnabled && (
		<MenuItem
			analytics={{ itemId: 'getting-started-page' }}
			href={`${urlPath}/getting-started`}
			iconBefore={<GettingStartedPageIcon spacing="spacious" label="" />}
			selectedOn={['servicedesk-getting-started-page']}
		>
			{formatMessage(messages.gettingStarted)}
		</MenuItem>
	);

	const developerEscalations = isDeveloperEscalationsEnabled && (
		<MenuItem
			analytics={{ itemId: 'developerEscalations' }}
			href={`${urlPath}/escalations`}
			iconBefore={<CodeIcon spacing="spacious" label="" />}
			selectedOn={['servicedesk-customer-service-escalations']}
		>
			{formatMessage(messages.itemDeveloperEscalations)}
		</MenuItem>
	);

	const products = isProductsAndEntitlementsEnabled && (
		<MenuItem
			analytics={{ itemId: 'products' }}
			href={`${urlPath}/products`}
			iconBefore={<LabelIcon spacing="spacious" label="" />}
			selectedOn={['servicedesk-customer-service-products']}
		>
			{formatMessage(messages.itemProducts)}
		</MenuItem>
	);

	const NotificationLogsMenuItem = hasNotificationLogs && (
		<MenuItem
			analytics={{ itemId: 'notificationLogs' }}
			href={`${urlPath}/notification-logs`}
			iconBefore={<EmailIcon spacing="spacious" label="" />}
			selectedOn={['notification-logs']}
		>
			{formatMessage(messages.notificationLogs)}
		</MenuItem>
	);

	const customersItem = features.hasCustomers && !isCustomerSupportManagementEnabled && (
		<MenuItem
			analytics={{ itemId: 'customers' }}
			href={`${urlPath}/customers`}
			iconBefore={<PeopleGroupIcon spacing="spacious" label="" />}
			selectedOn={['servicedesk-customers-v2']}
		>
			{formatMessage(messages.itemCustomers)}
		</MenuItem>
	);

	/* Please check for usage of this SpotlightTarget before changing
	 * http://go/src/CUSTOMER_CHANNELS_SPOTLIGHT|customer-channels-spotlight
	 */
	const customerChannelsItem = features.hasCustomerChannels && (
		<SpotlightTarget name={HOME_ITEMS_IDS.CUSTOMER_CHANNELS_SPOTLIGHT}>
			<CustomerChannelsItem>{formatMessage(messages.itemCustomerChannels)}</CustomerChannelsItem>
		</SpotlightTarget>
	);

	const inviteTeamItem =
		features.hasSettings && expValEquals('jsm_core_invites_experiment', 'cohort', 'experiment') ? (
			<InviteTeamItemNew>{formatMessage(messages.itemAddPeople)}</InviteTeamItemNew>
		) : (
			<InviteTeamItem loadEntryPointResource={loadInviteTeamResource}>
				{formatMessage(messages.itemInviteAgents)}
			</InviteTeamItem>
		);

	const vsaForAdmins =
		projectContext?.isProjectAdmin && fg('vsa-adoption-touchpoint-nav3') ? (
			<MenuItem
				analytics={{ itemId: 'virtualServiceAgent' }}
				href={generatePath(performanceRoute.path, { projectKey: project.key })}
				iconBefore={<AiAgentIcon label="" color="currentColor" />}
				iconAfter={<Lozenge appearance="new">{formatMessage(messages.vsaAdminLozenge)}</Lozenge>}
				data-testid="navigation-apps-sidebar-servicedesk.ui.menu-practices.vsa-for-admins"
			>
				{formatMessage(messages.vsaAdmin)}
			</MenuItem>
		) : null;

	const teamSupport =
		projectContext?.isProjectArchived === true ? null : (
			<Section
				title={formatMessage(
					fg('directory_to_customers_rename') ? messages.customers : messages.directory,
				)}
			>
				<pre />
				{fg('jsm_customers_directory_page_for_all')
					? features.hasCustomers && <CustomerSupportManagementItems urlPath={urlPath} />
					: isCustomerSupportManagementEnabled && (
							<CustomerSupportManagementItems urlPath={urlPath} />
						)}
				{products}
				{customerChannelsItem}
				{!fg('jsm_customers_directory_page_for_all') && customersItem}
				{stack.length === 0 && isInviteTeamSpotlightEnabled ? (
					<SpotlightManager blanketIsTinted={false}>
						<SpotlightTarget name={INVITE_USERS_PROMPTS_KEY}>{inviteTeamItem}</SpotlightTarget>
						<InviteUserPrompt onCTAClick={onInviteTeamOnboardingCTAClick} />
					</SpotlightManager>
				) : (
					inviteTeamItem
				)}
			</Section>
		);
	const ssrPlaceholderEnabledRoutes: RouteName[] = [
		'servicedesk-queues-custom-view',
		'servicedesk-practice-custom-and-issue',
	];

	return (
		// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string[] | undefined'.
		<ContextualAnalyticsData menuId={MENU_ID_SD_ITSM} stack={stack}>
			<SideNavigation
				label=""
				isServer={__SERVER__}
				isSSRPlaceholderEnabled={
					fg('add_ssr_placeholder_replacements_to_queues_nav')
						? ssrPlaceholderEnabledRoutes.includes(route.name)
						: undefined
				}
			>
				<NavigationHeader>
					<ProjectHeader
						description={formatMessage(messages.headerDescriptionJsm)}
						avatarUrl={project.avatarUrl}
						name={project.name}
					/>
				</NavigationHeader>
				{/* @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string[] | undefined'. | TS2322 - Type 'unknown' is not assignable to type '((stack: string[]) => void) | undefined'. */}
				<NestableNavigationContent stack={stack} onChange={setStack} isDefaultFocusControl={false}>
					{/* Please check for usage of this SpotlightTarget before changing
					 * http://go/src/ALL_ITSM_QUEUES_SPOTLIGHT|all-itsm-queues-spotlight
					 */}
					{/* @ts-expect-error - TS2322 - Type 'false | Element' is not assignable to type 'Element'. */}
					<SpotlightTarget name={HOME_ITEMS_IDS.ALL_ITSM_QUEUES_SPOTLIGHT}>
						<Section>
							{GettingStartedPageMenuItem}
							{jsmProjectSummary}
							{shouldRenderNesting(
								practiceList.includes(NO_CATEGORY),
								// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'.
								stack,
								SECTION_ID.QUEUES,
							) && (
								<NestingMenuItem
									analytics={{ itemId: 'queues' }}
									href={`${urlPath}/${QUEUE_ROUTES[NO_CATEGORY]}`}
									iconBefore={<QueuesIcon spacing="spacious" label="" />}
									id={SECTION_ID.QUEUES}
									overrides={{ BackMenuItem }}
									selectedOn={matchPartialUrlPathname()}
									title={formatMessage(messages.itemQueuesRetranslate)}
									// @ts-expect-error - TS7006 - Parameter 'ev' implicitly has an 'any' type.
									onClick={(ev) => ev.preventDefault()}
									data-testid="navigation-apps-sidebar-servicedesk.ui.menu-practices.nesting-menu-item"
								>
									<PracticeQueues
										practice={NO_CATEGORY}
										title={formatMessage(practiceMessages.queues)}
									/>
								</NestingMenuItem>
							)}
							{jsmViews}
							<>
								{shouldRenderNesting(
									practiceList.includes(SERVICE_REQUEST),
									// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'.
									stack,
									SERVICE_REQUEST,
								) && (
									<NestingMenuItem
										analytics={{ itemId: 'service-requests' }}
										href={`${urlPath}/${QUEUE_ROUTES[SERVICE_REQUEST]}`}
										// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
										iconBefore={<Icon glyph={ServiceRequestsGlyph} label="" />}
										id={SERVICE_REQUEST}
										overrides={{ BackMenuItem }}
										selectedOn={matchPartialUrlPathname()}
										title={formatMessage(practiceMessages.serviceRequests)}
										// @ts-expect-error - TS7006 - Parameter 'ev' implicitly has an 'any' type.
										onClick={(ev) => ev.preventDefault()}
									>
										<PracticeQueues
											practice={SERVICE_REQUEST}
											title={formatMessage(practiceMessages.serviceRequests)}
										/>
									</NestingMenuItem>
								)}
								{shouldRenderNesting(
									practiceList.includes(INCIDENTS),
									// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'.
									stack,
									INCIDENTS,
								) && (
									<NestingMenuItem
										analytics={{ itemId: 'incidents' }}
										href={`${urlPath}/${QUEUE_ROUTES[INCIDENTS]}`}
										// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
										iconBefore={<Icon glyph={IncidentsGlyph} label="" />}
										id={INCIDENTS}
										overrides={{ BackMenuItem }}
										selectedOn={matchPartialUrlPathname()}
										title={formatMessage(practiceMessages.incidents)}
										// @ts-expect-error - TS7006 - Parameter 'ev' implicitly has an 'any' type.
										onClick={(ev) => ev.preventDefault()}
									>
										<PracticeQueues
											title={formatMessage(practiceMessages.incidents)}
											practice={INCIDENTS}
										/>
									</NestingMenuItem>
								)}
								{shouldRenderNesting(
									practiceList.includes(PROBLEMS),
									// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'.
									stack,
									PROBLEMS,
								) && (
									<NestingMenuItem
										analytics={{ itemId: 'problems' }}
										href={`${urlPath}/${QUEUE_ROUTES[PROBLEMS]}`}
										// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
										iconBefore={<Icon glyph={ProblemsGlyph} label="" />}
										id={PROBLEMS}
										overrides={{ BackMenuItem }}
										selectedOn={matchPartialUrlPathname()}
										title={formatMessage(practiceMessages.problems)}
										// @ts-expect-error - TS7006 - Parameter 'ev' implicitly has an 'any' type.
										onClick={(ev) => ev.preventDefault()}
									>
										<PracticeQueues
											practice={PROBLEMS}
											title={formatMessage(practiceMessages.problems)}
										/>
									</NestingMenuItem>
								)}
								{shouldRenderNesting(
									practiceList.includes(CHANGES),
									// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'.
									stack,
									CHANGES,
								) && (
									<NestingMenuItem
										analytics={{ itemId: 'changes' }}
										href={`${urlPath}/${QUEUE_ROUTES[CHANGES]}`}
										// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
										iconBefore={<Icon glyph={ChangesGlyph} label="" />}
										id={CHANGES}
										overrides={{ BackMenuItem }}
										selectedOn={matchPartialUrlPathname()}
										title={formatMessage(practiceMessages.changes)}
										// @ts-expect-error - TS7006 - Parameter 'ev' implicitly has an 'any' type.
										onClick={(ev) => ev.preventDefault()}
									>
										<PracticeQueues
											practice={CHANGES}
											title={formatMessage(practiceMessages.changes)}
										/>
									</NestingMenuItem>
								)}
								{shouldRenderNesting(
									practiceList.includes(POST_INCIDENT_REVIEWS),
									// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'.
									stack,
									POST_INCIDENT_REVIEWS,
								) && (
									<NestingMenuItem
										analytics={{ itemId: 'post-incident-reviews' }}
										href={`${urlPath}/${QUEUE_ROUTES[POST_INCIDENT_REVIEWS]}`}
										iconBefore={
											// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
											<Icon glyph={PostIncidentReviewsGlyph} label="" />
										}
										id={POST_INCIDENT_REVIEWS}
										overrides={{ BackMenuItem }}
										selectedOn={matchPartialUrlPathname()}
										title={formatMessage(practiceMessages.postIncidentReviews)}
										// @ts-expect-error - TS7006 - Parameter 'ev' implicitly has an 'any' type.
										onClick={(ev) => ev.preventDefault()}
									>
										<PracticeQueues
											practice={POST_INCIDENT_REVIEWS}
											title={formatMessage(practiceMessages.postIncidentReviews)}
										/>
									</NestingMenuItem>
								)}
							</>
							{developerEscalations}
						</Section>
					</SpotlightTarget>

					{hasOperationsSectionItems && (
						<Section title={formatMessage(messages.operations)}>
							{changeCalendar}
							{services}
							{(shouldShowOpsGenieAlert || shouldShowOpsGenieOnCall) && (
								/* Please check for usage of this SpotlightTarget before changing
								 * http://go/src/ALERTS_AND_ON_CALL_SPOTLIGHT|alerts-and-on-call-spotlight
								 */
								<SpotlightTarget name={HOME_ITEMS_IDS.ALERTS_AND_ON_CALL_SPOTLIGHT}>
									{shouldShowOpsGenieAlert && (
										<MenuItem
											analytics={{ itemId: 'opsgenie-alert' }}
											css={styles.menuItem}
											// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
											iconBefore={<Icon glyph={AlertsGlyph} label="" />}
											href="/jira/ops/alerts?view=list&query="
											iconAfter={<ShortcutIcon spacing="spacious" label="" />}
											target="_blank"
											{...(!isJSMAlertOnCallEnabled && {
												href: features.opsgenieAlert.url,
											})}
										>
											{formatMessage(messages.alerts)}
											<ScreenReaderText>
												{formatMessage(messages.linkOpensInNewWindow)}
											</ScreenReaderText>
										</MenuItem>
									)}
									{shouldShowOpsGenieOnCall && (
										<MenuItem
											analytics={{ itemId: 'opsgenie-on-call' }}
											css={styles.menuItem}
											// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
											iconBefore={<Icon glyph={OnCallGlyph} label="" />}
											href="/jira/ops/who-is-on-call"
											selectedOn={matchPartialUrlPathname()}
											iconAfter={<ShortcutIcon spacing="spacious" label="" />}
											target="_blank"
											{...(!isJSMAlertOnCallEnabled && {
												href: `${urlPath}/on-call-schedule`,
												iconAfter: undefined,
												target: undefined,
											})}
										>
											{formatMessage(messages.onCall)}
										</MenuItem>
									)}
								</SpotlightTarget>
							)}
						</Section>
					)}
					<Section title={knowledgeInsightsTitle}>
						{vsaForAdmins}
						{shouldRenderNesting(
							features.knowledge.enabled && !!features.knowledge.type,
							// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'.
							stack,
							'knowledge',
						) && (
							<KnowledgeItem
								href={`${urlPath}/knowledge`}
								nestingId="knowledge"
								overrides={{ BackMenuItem }}
								// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string[]'.
								stack={stack}
								type={features.knowledge.type}
							/>
						)}
						{/* @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'. */}
						{shouldRenderNesting(features.hasReports, stack, SECTION_ID.REPORTS) && (
							<NestingMenuItem
								analytics={{ itemId: 'reports' }}
								href={`${urlPath}/reports`}
								iconBefore={<ReportsIcon spacing="spacious" label="" />}
								id={SECTION_ID.REPORTS}
								overrides={{ BackMenuItem }}
								selectedOn={matchPartialUrlPathname()}
								title={formatMessage(messages.itemReports)}
							>
								<Reports projectKey={project.key} connectItems={connectReportsItems} />
							</NestingMenuItem>
						)}

						{teamSupport}

						{/* @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'. */}
						{shouldRenderNesting(features.hasPIN, stack, SECTION_ID.ISSUES) && (
							<IssuesContextualAnalyticsData navigatorScope="project">
								<NestingMenuItem
									analytics={{ itemId: 'project-issue-navigator' }}
									href={`/jira/servicedesk/projects/${project.key}/issues`}
									iconBefore={<IssuesIcon spacing="spacious" label="" />}
									id={SECTION_ID.ISSUES}
									overrides={{ BackMenuItem }}
									selectedOn={matchPartialUrlPathname()}
									title={formatMessage(messages.itemIssues)}
								>
									<Issues
										projectKey={project.key}
										pinUrl={`/jira/servicedesk/projects/${project.key}/issues/?filter=`}
									/>
								</NestingMenuItem>
							</IssuesContextualAnalyticsData>
						)}
					</Section>
					<ForgeProjectSection overrides={{ BackMenuItem }} />
					<ConnectMenu
						hasSeparator
						items={projectConnectItems}
						showIcons
						overrides={{ BackMenuItem }}
					/>
					{/* @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type 'Element'. */}
					<Section title={formatMessage(messages.shortcuts)}>
						<Shortcuts
							addItemLabel={formatMessage(messages.addShortcutLabel)}
							canAddItem={features.hasShortcutsAdd}
							canAddLinks
							canAddRepositories={false}
							isSimplifiedProject={project.isSimplified}
							projectKey={project.key}
							projectId={project.id}
						/>
					</Section>
					{/* @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type 'Element'. */}
					<Section hasSeparator>
						{/* @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string[]'. */}
						{shouldRenderNesting(features.hasSettings, stack, SECTION_ID.SETTINGS) && (
							<>
								<NestingMenuItem
									analytics={{ itemId: 'settings' }}
									href={classicSettingsUrl}
									iconBefore={<IconSettings spacing="spacious" label="" />}
									id={SECTION_ID.SETTINGS}
									overrides={{
										BackMenuItem: omit(BackMenuItem, ['onClick']),
									}}
									selectedOn={matchPartialUrlPathname()}
									title={formatMessage(messages.projectSettingsRetranslate)}
								>
									<SettingsClassic
										ecosystemItems={ecosystemItems}
										projectId={project.id}
										projectKey={project.key}
										// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string[]'.
										stack={stack}
									/>
								</NestingMenuItem>
								{NotificationLogsMenuItem}
								{feedback}
							</>
						)}
					</Section>
					{fg('ken_2264_archive_menu_itsm_project') ? archivedProjectItems : null}
				</NestableNavigationContent>
			</SideNavigation>
		</ContextualAnalyticsData>
	);
};
